import { OrderedSet, List } from 'immutable';
import { Mark, Block, MarkProperties } from 'slate';
import { STYLE_TYPES, MARK_TYPES, TEXT_ALIGN } from './constants';
import { colorMarkClassName } from '../../../../containers/Editor/constants';

export const getMarksByType = (marks: OrderedSet<Mark>, type: STYLE_TYPES) => (
	marks.filter((mark: Mark) => mark.type === type)
);

const getMark = (type: STYLE_TYPES, style: {}, className?: string): MarkProperties => ({
	type,
	data: { style, className },
});

export const getFontFamily = (allMarks: OrderedSet<Mark>): string => {
	const marks = getMarksByType(allMarks, MARK_TYPES.get(STYLE_TYPES.FONT_FAMILY) as STYLE_TYPES);

	if (!marks.size) {
		return '';
	}

	const { fontFamily } = marks.first().data.get('style');
	return fontFamily;
};

export const getFontSize = (allMarks: OrderedSet<Mark>): string => {
	const marks = getMarksByType(allMarks, MARK_TYPES.get(STYLE_TYPES.FONT_SIZE) as STYLE_TYPES);

	if (!marks.size) {
		return '';
	}

	const { fontSize } = marks.first().data.get('style');
	return fontSize;
};

export const getLetterSpacing = (allMarks: OrderedSet<Mark>): string => {
	const marks = getMarksByType(allMarks, MARK_TYPES.get(STYLE_TYPES.LETTER_SPACING) as STYLE_TYPES);

	if (!marks.size) {
		return '0px';
	}

	const { letterSpacing } = marks.first().data.get('style');
	return letterSpacing;
};

export const getColor = (allMarks: OrderedSet<Mark>): string => {
	const marks = getMarksByType(allMarks, MARK_TYPES.get(STYLE_TYPES.COLOR) as STYLE_TYPES);

	if (!marks.size) {
		return '';
	}

	const { color } = marks.first().data.get('style');
	return color;
};

export const getLineHeight = (blocks: List<Block>): string => {
	const style = blocks.first().data.get('style');

	if (!blocks.size || !style) {
		return '';
	}

	return style.lineHeight;
};

export const isFontWeightBold = (activeMarks: OrderedSet<Mark>): boolean => {
	const marks = getMarksByType(activeMarks, MARK_TYPES.get(STYLE_TYPES.FONT_WEIGHT) as STYLE_TYPES);
	return marks.size > 0 && marks.some((mark: Mark) => {
		const { fontWeight } = mark.data.get('style');
		return fontWeight === 'bold' || fontWeight >= 700;
	});
};

export const isFontStyleItalic = (activeMarks: OrderedSet<Mark>): boolean => {
	const marks = getMarksByType(activeMarks, MARK_TYPES.get(STYLE_TYPES.FONT_STYLE) as STYLE_TYPES);
	return marks.size > 0 && marks.some((mark: Mark) => (
		mark.data.get('style').fontStyle === 'italic'
	));
};

export const isTextDecorationUnderline = (activeMarks: OrderedSet<Mark>): boolean => {
	const marks = getMarksByType(activeMarks, MARK_TYPES.get(STYLE_TYPES.TEXT_DECORATION) as STYLE_TYPES);
	return marks.size > 0 && marks.some((mark: Mark) => (
		mark.data.get('style').textDecoration === 'underline'
	));
};

const checkTextAlign = (blocks: List<Block>, textAlign: string): boolean => {
	if (!blocks.size) {
		return false;
	}

	return blocks.every((block: Block) => {
		const style = block.data.get('style');
		return !!style && style.textAlign === textAlign;
	});
};

export const isTextAlignLeft = (blocks: List<Block>): boolean => checkTextAlign(blocks, TEXT_ALIGN.LEFT);
export const isTextAlignCenter = (blocks: List<Block>): boolean => checkTextAlign(blocks, TEXT_ALIGN.CENTER);
export const isTextAlignRight = (blocks: List<Block>): boolean => checkTextAlign(blocks, TEXT_ALIGN.RIGHT);
export const isTextAlignJustify = (blocks: List<Block>): boolean => checkTextAlign(blocks, TEXT_ALIGN.JUSTIFY);

export const getFontFamilyMark = (fontFamily: string): MarkProperties => (
	getMark(MARK_TYPES.get(STYLE_TYPES.FONT_FAMILY) as STYLE_TYPES, { fontFamily })
);

export const getFontSizeMark = (fontSize: string): MarkProperties => (
	getMark(MARK_TYPES.get(STYLE_TYPES.FONT_SIZE) as STYLE_TYPES, { fontSize })
);

export const getColorMark = (color: string): MarkProperties => (
	getMark(MARK_TYPES.get(STYLE_TYPES.COLOR) as STYLE_TYPES, { color }, colorMarkClassName)
);

export const getFontWeightMark = (activeMarks: OrderedSet<Mark>): MarkProperties => {
	const fontWeight = isFontWeightBold(activeMarks) ? 'normal' : 'bold';
	return getMark(MARK_TYPES.get(STYLE_TYPES.FONT_WEIGHT) as STYLE_TYPES, { fontWeight });
};

export const getFontStyleMark = (activeMarks: OrderedSet<Mark>): MarkProperties => {
	const fontStyle = isFontStyleItalic(activeMarks) ? 'normal' : 'italic';
	return getMark(MARK_TYPES.get(STYLE_TYPES.FONT_STYLE) as STYLE_TYPES, { fontStyle });
};

export const getTextDecorationMark = (activeMarks: OrderedSet<Mark>): MarkProperties => {
	const textDecoration = isTextDecorationUnderline(activeMarks) ? 'none' : 'underline';
	return getMark(MARK_TYPES.get(STYLE_TYPES.TEXT_DECORATION) as STYLE_TYPES, { textDecoration });
};

export const getLetterSpacingMark = (letterSpacing: string): MarkProperties => (
	getMark(MARK_TYPES.get(STYLE_TYPES.LETTER_SPACING) as STYLE_TYPES, { letterSpacing })
);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/RichTextEditor/helpers/marks.ts